import React from "react";
import { MdEdit, MdDelete, MdPushPin } from "react-icons/md";
import Card from "../../../../components/card";
import { NewsPost } from "../../../../interfaces/News";
import { categoryOf, publishedLabel } from "../categories";
import { isScheduled } from "../serverDate";

interface Props {
  posts: NewsPost[];
  onEdit: (post: NewsPost) => void;
  onDelete: (post: NewsPost) => void;
}

/** Lista de quem publica: mostra o que o feed esconde — rascunho, agendado e expirado. */
const NewsAdminList: React.FC<Props> = ({ posts, onEdit, onDelete }) => {
  if (posts.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center dark:border-white/10">
        <p className="font-display text-base font-bold text-navy-700 dark:text-white">
          Nada publicado ainda
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          O primeiro comunicado chega à empresa inteira — portal e widget — assim que você publicar.
        </p>
      </div>
    );
  }

  return (
    <Card extra="p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-gray-50 dark:bg-navy-900">
            <tr>
              {["Comunicado", "Editoria", "Publicação", "Leituras", ""].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => {
              const meta = categoryOf(p.category);
              const expired = !!p.expiresAt && new Date(p.expiresAt) < new Date();
              return (
                <tr
                  key={p.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {p.pinned && (
                        <MdPushPin className="h-3.5 w-3.5 text-gray-400" aria-label="Fixado" />
                      )}
                      <span className="font-semibold text-navy-700 dark:text-white">{p.title}</span>
                    </div>
                    {p.summary && (
                      <p className="mt-0.5 max-w-md truncate text-xs text-gray-500 dark:text-gray-400">
                        {p.summary}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.chip}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {publishedLabel(p.publishedAt)}
                    {isScheduled(p.publishedAt) && (
                      <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        agendado
                      </span>
                    )}
                    {expired && (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                        expirado
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                    {p.readCount}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(p)}
                        aria-label={`Editar ${p.title}`}
                        className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700"
                      >
                        <MdEdit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(p)}
                        aria-label={`Excluir ${p.title}`}
                        className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/20"
                      >
                        <MdDelete className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default NewsAdminList;
